import React from 'react';
import { useTranslation } from 'react-i18next';

interface PaginationProps {
  /**
   * Current page number (1-based)
   */
  currentPage: number;
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Function to handle page changes
   */
  paginate: (pageNumber: number) => void;
  /**
   * Number of items per page
   */
  itemsPerPage: number;
  /**
   * Total number of items being paginated
   */
  totalItems: number;
  /**
   * Available options for items per page selector
   */
  itemsPerPageOptions: number[];
  /**
   * Function to handle changing the number of items per page
   */
  handleItemsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /**
   * Whether to show the items per page selector (default: true)
   */
  showItemsPerPage?: boolean;
  /**
   * Whether to show the summary text (default: true)
   */
  showSummary?: boolean;
  /**
   * ARIA label for the pagination navigation
   */
  ariaLabel?: string;
}

/**
 * Reusable pagination component that displays pagination controls and items per page selector.
 * 
 * Features:
 * - Responsive design that works well on mobile and desktop
 * - Configurable items per page with dropdown
 * - Page summary showing current range and total items
 * - Accessible navigation with ARIA attributes
 * - Smart pagination that shows first/last pages with ellipses for large page counts
 * 
 * This component should be used together with the usePagination hook for a complete solution.
 * 
 * @example
 * <Pagination 
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   paginate={paginate}
 *   itemsPerPage={itemsPerPage}
 *   totalItems={items.length}
 *   itemsPerPageOptions={itemsPerPageOptions}
 *   handleItemsPerPageChange={handleItemsPerPageChange}
 *   ariaLabel="Items pagination"
 * />
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  paginate,
  itemsPerPage,
  totalItems,
  itemsPerPageOptions,
  handleItemsPerPageChange,
  showItemsPerPage = true,
  showSummary = true,
  ariaLabel = 'Pagination'
}) => {
  const { t } = useTranslation();
  
  // Don't render pagination if there's only one page
  if (totalPages <= 1 && !showSummary && !showItemsPerPage) {
    return null;
  }

  // Calculate current page range for summary
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="border-top pt-3">
      {/* Summary and items per page selector */}
      {(showSummary || showItemsPerPage) && (
        <div className="row align-items-center mb-2">
          {showSummary && (
            <div className="col-12 col-sm-6 mb-2 mb-sm-0 text-center text-sm-start">
              <small className="text-body-secondary">
                {t('pagination.showing', 'Showing {{start}} to {{end}} of {{total}} items', {
                  start: startItem,
                  end: endItem,
                  total: totalItems
                })}
              </small>
            </div>
          )}
          
          {showItemsPerPage && (
            <div className={`col-12 ${showSummary ? 'col-sm-6 text-center text-sm-end' : ''}`}>
              <select 
                className="form-select form-select-sm d-inline-block w-auto" 
                value={itemsPerPage} 
                onChange={handleItemsPerPageChange}
                aria-label={t('pagination.itemsPerPage', 'Items per page')}
              >
                {itemsPerPageOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <small className="ms-2 text-body-secondary d-none d-sm-inline">
                {t('pagination.itemsPerPage', 'items per page')}
              </small>
            </div>
          )}
        </div>
      )}
      
      {/* Page navigation */}
      {totalPages > 1 && (
        <nav aria-label={ariaLabel} className="d-flex justify-content-center mt-3">
          <ul className="pagination pagination-sm mb-0">
            {/* Previous button */}
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label={t('pagination.previous', 'Previous page')}
              >
                <i className="bi bi-chevron-left" aria-hidden="true"></i>
              </button>
            </li>
            
            {/* Page buttons with ellipses for large number of pages */}
            {(() => {
              // Max page buttons to show - fewer on mobile
              const maxButtons = 3;
              // Always show first and last page
              const showFirstPage = true;
              const showLastPage = true;
              // Calculate range to display around current page
              let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
              const endPage = Math.min(totalPages, startPage + maxButtons - 1);
              
              // Adjust if we're near the end
              if (endPage - startPage + 1 < maxButtons) {
                startPage = Math.max(1, endPage - maxButtons + 1);
              }
              
              // Build page buttons array
              const pages = [];
              
              // First page (if not in range)
              if (showFirstPage && startPage > 1) {
                pages.push(
                  <li key="first" className="page-item">
                    <button
                      className="page-link"
                      onClick={() => paginate(1)}
                      aria-label={t('pagination.page', 'Page {{page}}', { page: 1 })}
                    >
                      1
                    </button>
                  </li>
                );
                
                // Ellipsis after first page (if needed)
                if (startPage > 2) {
                  pages.push(
                    <li key="ellipsis1" className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }
              }
              
              // Main page number buttons
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => paginate(i)}
                      aria-label={t('pagination.page', 'Page {{page}}', { page: i })}
                      aria-current={currentPage === i ? 'page' : undefined}
                    >
                      {i}
                    </button>
                  </li>
                );
              }
              
              // Last page (if not in range)
              if (showLastPage && endPage < totalPages) {
                // Ellipsis before last page (if needed)
                if (endPage < totalPages - 1) {
                  pages.push(
                    <li key="ellipsis2" className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }
                
                pages.push(
                  <li key="last" className="page-item">
                    <button
                      className="page-link"
                      onClick={() => paginate(totalPages)}
                      aria-label={t('pagination.page', 'Page {{page}}', { page: totalPages })}
                    >
                      {totalPages}
                    </button>
                  </li>
                );
              }
              
              return pages;
            })()}
            
            {/* Next button */}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label={t('pagination.next', 'Next page')}
              >
                <i className="bi bi-chevron-right" aria-hidden="true"></i>
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default Pagination; 